import React, { FC, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

export interface EvenementBtns3Props {
  index: string;
  onBlocNotesClick?: (typeEvenement: string, idEvenement: number) => void;
  onConfirmClick?: (typeEvenement: string, idEvenement: number) => void;
  onInfoClick?: (
    placeId: string,
    placeNom: string,
    latitude: string,
    longitude: string
  ) => void;
  onRechercheClick?: (typeEvenement: string, idEvenement: number) => void;
  lienVisible?: string;
  lienItineraire?: string;
  placeId?: string;
  placeNom?: string;
  latitude?: string;
  longitude?: string;
  className?: string;
  optionsVisibles?: boolean;
}

const EvenementBtns3: FC<EvenementBtns3Props> = ({
  index,
  onBlocNotesClick,
  onConfirmClick,
  onInfoClick,
  onRechercheClick,
  lienVisible,
  lienItineraire,
  placeId,
  placeNom,
  latitude,
  longitude,
  className = "right-2 top-2",
  optionsVisibles,
}) => {
  const { t } = useTranslation();
  const location = useLocation();

  const isIntegrated =
    location.pathname.startsWith("/itineraire-voyage-integre") ||
    location.pathname.startsWith("/en/travel-itinerary-embed");

  const [isPopupVisible, setPopupVisible] = useState(false);
  const popupRef = useRef<HTMLDivElement>(null);

  const typeEvenement = index.split("-")[0];
  const idEvenement = Number(index.split("-")[1]);

  const hasActions =
    onRechercheClick ||
    onBlocNotesClick ||
    onConfirmClick ||
    onInfoClick ||
    lienVisible ||
    lienItineraire;

  useEffect(() => {
    setPopupVisible(optionsVisibles || false);
  }, [optionsVisibles]);

  const showPopup = () => {
    setPopupVisible(true);
  };

  const handleMouseLeave: React.MouseEventHandler<HTMLDivElement> = (event) => {
    const nextTarget = event.relatedTarget as Node | null;

    if (!nextTarget || !popupRef.current?.contains(nextTarget)) {
      setPopupVisible(false);
    }
  };

  const handleInfoClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (onInfoClick && placeId && placeNom) {
      onInfoClick(
        placeId,
        placeNom,
        latitude || "",
        longitude || ""
      );
    }
  };

  const handleConfirmClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (onConfirmClick) {
      onConfirmClick(typeEvenement, idEvenement);
    }
  };

  const handleBlocClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (onBlocNotesClick) {
      onBlocNotesClick(typeEvenement, idEvenement);
    }
  };

  const handleRechercheClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (onRechercheClick) {
      onRechercheClick(typeEvenement, idEvenement);
    }
  };

  const popupWidthClass = "w-[180px]";

  const actionClass = `
    flex
    w-full
    items-center
    justify-between
    gap-3

    px-3
    py-2.5

    rounded-xl

    ${isIntegrated ? "text-xxs" : "text-xs"}
    font-medium
    text-neutral-900
    dark:text-white

    transition-colors
    duration-150

    hover:bg-neutral-50
    dark:hover:bg-neutral-800
  `;

  return (
    <div
      className={`absolute z-[30] ${className}`}
      onMouseOver={showPopup}
      onMouseLeave={handleMouseLeave}
    >
      {isPopupVisible && hasActions && (
        <div
          ref={popupRef}
          className={`
            absolute
            right-0
            top-0
            z-[99999]

            ${popupWidthClass}

            rounded-2xl

            border
            border-neutral-100
            dark:border-neutral-800

            bg-white
            dark:bg-neutral-900

            p-1

            text-left

            shadow-xl
          `}
        >
          <div
            className="
              px-3
              py-1

              text-[10px]
              font-bold
              uppercase
              tracking-[0.08em]

              text-neutral-400
              dark:text-neutral-500
            "
          >
            Options
          </div>

          {onRechercheClick && (
            <button
              type="button"
              className={actionClass}
              onClick={handleRechercheClick}
            >
              <span className="flex items-center gap-1">
                <i className="las la-search text-xs text-rose-500" />
                {t("reservation")}
              </span>

              <i className="las la-angle-right text-neutral-300" />
            </button>
          )}

          {onBlocNotesClick && (
            <button
              type="button"
              className={actionClass}
              onClick={handleBlocClick}
            >
              <span className="flex items-center gap-1">
                <i className="las la-pencil-alt text-xs text-amber-500" />
                {t("blocNotes")}
              </span>

              <i className="las la-angle-right text-neutral-300" />
            </button>
          )}

          {onConfirmClick && (
            <button
              type="button"
              className={actionClass}
              onClick={handleConfirmClick}
            >
              <span className="flex items-center gap-1">
                <i className="las la-file-alt text-xs text-blue-500" />
                {t("confirmation")}
              </span>

              <i className="las la-angle-right text-neutral-300" />
            </button>
          )}

          {onInfoClick && (
            <button
              type="button"
              className={actionClass}
              onClick={handleInfoClick}
            >
              <span className="flex items-center gap-1">
                <i className="las la-info-circle text-xs text-violet-500" />
                {t("informations")}
              </span>

              <i className="las la-angle-right text-neutral-300" />
            </button>
          )}

          {lienItineraire && (
            <a
              href={lienItineraire}
              target="_blank"
              rel="noreferrer"
              className={actionClass}
              onClick={(event) => event.stopPropagation()}
            >
              <span className="flex items-center gap-1">
                <i className="las la-globe text-xs text-neutral-700 dark:text-neutral-300" />
                {t("itineraire")}
              </span>

              <i className="las la-angle-right text-neutral-300" />
            </a>
          )}

          {lienVisible && (
            <a
              href={lienVisible}
              target="_blank"
              rel="noreferrer"
              className={actionClass}
              onClick={(event) => event.stopPropagation()}
            >
              <span className="flex items-center gap-1">
                <i className="las la-link text-xs text-neutral-700 dark:text-neutral-300" />
                {t("lien")}
              </span>

              <i className="las la-angle-right text-neutral-300" />
            </a>
          )}
        </div>
      )}
    </div>
  );
};

export default EvenementBtns3;
